import { defineStore } from 'pinia'
import { store } from '../index'
import { enableStoreHMRUpdate } from '../helper'
import {
  getWarehouseCode,
  /* clearWarehouseCode, */
  setWarehouseCode,
  getPhysicalWarehouseInfo,
  setPhysicalWarehouseInfo,
} from '@/utils/warehouseCode'
import {
  type WarehouseModel,
  type PhysicalWarehouseListItem,
  getWarehouseDetail,
  changePhysicalWarehouseApi,
} from '@/api/common'
import { useLocaleStoreWithOut } from '@/store-pinia/modules/locale'
import useCommonDataSource from '@/composables/useCommonDataSource'
import { useUserStoreWithOut } from '@/store-pinia/modules/user'
import { TimezoneType, getTimezoneType, setTimezoneType } from '@/utils/timezoneCache'
import { useWarehouseSwitch } from '@/composables/useWarehouseSwitch'
import {
  getPhysicalWarehouseList,
  getAllPhysicalWarehouseList,
  getAllRegionWarehouseList,
} from '@/api/common'
import {
  setUserPhysicalWarehouseList,
  clearUserPhysicalWarehouseList,
  setAllPhysicalWarehouseList,
  clearAllPhysicalWarehouseList,
  clearALLRegionWarehouseStorage,
  setAllRegionWarehouseList,
} from '@/utils/auth'

export interface State {
  pageLoading: boolean
  warehouseCode: string
  warehouseInfo: Nullable<WarehouseModel>
  physicalWarehouseInfo: Nullable<PhysicalWarehouseListItem>
  physicalWarehouseList: PhysicalWarehouseListItem[]
  changePhysicalWarehouseTime: number
  currentTimezoneType: TimezoneType
  choseWarehouse: string
}

/**
 * 基础store
 */
let timer: ReturnType<typeof setTimeout>
export const useBaseStore = defineStore('baseStore', {
  state: (): State => ({
    pageLoading: false,
    warehouseCode: getWarehouseCode() || '',
    warehouseInfo: null,
    physicalWarehouseInfo: getPhysicalWarehouseInfo(),
    physicalWarehouseList: [],
    changePhysicalWarehouseTime: 0,
    currentTimezoneType: getTimezoneType() || TimezoneType.WH,
    choseWarehouse: '',
  }),
  getters: {
    getPageLoading: (state) => state.pageLoading,
    getWarehouseCode: (state) => state.warehouseCode,
    getWarehouseInfo: (state) => state.warehouseInfo,
    getPhysicalWarehouseInfo: (state) => state.physicalWarehouseInfo,
    getSelectedWarehouseText: (state) =>
      (state.warehouseInfo?.regionWarehouseName || '') +
      '-' +
      state.physicalWarehouseInfo?.physicsWarehouseName,
    getPhysicalWarehouseList: (state) => state.physicalWarehouseList,
    getRegionWarehouseText: (state) => {
      const { getLocale } = useLocaleStoreWithOut()

      return `${state.warehouseInfo?.regionWarehouseCode} [${
        getLocale === 'zh_CN'
          ? state.warehouseInfo?.regionWarehouseNameCn
          : state.warehouseInfo?.regionWarehouseNameEn
      }]`
    },
  },
  actions: {
    /**
     * 重置state
     */
    resetState() {
      this.pageLoading = false
      this.warehouseInfo = null
      this.physicalWarehouseInfo = null
      this.changePhysicalWarehouseTime = 0
    },
    setPageLoading(loading: boolean): void {
      this.pageLoading = loading
    },
    setWarehouseCode(code: string) {
      this.warehouseCode = code
      code && setWarehouseCode(code)
    },
    setWarehouseInfo(info: WarehouseModel | null) {
      this.warehouseInfo = info
    },
    setPhysicalWarehouseInfo(info: PhysicalWarehouseListItem | null) {
      this.physicalWarehouseInfo = info
      info && setPhysicalWarehouseInfo(info)
    },
    setCurrentTimezoneType(timezoneType: TimezoneType) {
      this.currentTimezoneType = timezoneType
      setTimezoneType(timezoneType)
    },
    setChoseWarehouse(warehouse: string) {
      this.choseWarehouse = warehouse || this.getRegionWarehouseText
    },
    async changePageLoading(loading: boolean): Promise<void> {
      if (loading) {
        clearTimeout(timer)
        timer = setTimeout(() => {
          this.setPageLoading(loading)
        }, 50)
      } else {
        this.setPageLoading(loading)
        clearTimeout(timer)
      }
    },
    async initWarehouseInfo() {
      const { getLocale } = useLocaleStoreWithOut()
      try {
        const info = await getWarehouseDetail({ regionWarehouseCode: this.warehouseCode })
        info.regionWarehouseName =
          getLocale === 'zh_CN' ? info.regionWarehouseNameCn : info.regionWarehouseNameEn
        this.setWarehouseInfo(info)

        return info
      } catch (err) {
        // this.setWarehouseInfo(null)
        // this.setWarehouseCode('')
        // clearWarehouseCode()
        return Promise.reject(err)
      }
    },
    async initPhysicalWarehouseList() {
      const { getPhysicalWarehouses } = useCommonDataSource()
      try {
        const result = await getPhysicalWarehouses({
          regionWarehouseCode: this.warehouseCode,
        })
        this.physicalWarehouseList = result
        return result
      } catch (err) {
        return Promise.reject(err)
      }
    },
    async changePhysicalWarehouse() {
      if (this.changePhysicalWarehouseTime === 0) {
        const userStore = useUserStoreWithOut()
        const userInfo = userStore.getUserInfo
        // await this.initPhysicalWarehouseList()
        const found = userInfo?.physicsWarehouseList.find(
          (item) => item.physicsWarehouseCode === this.physicalWarehouseInfo?.physicsWarehouseCode,
        )
        const physicalWarehoue = found || userInfo?.physicsWarehouseList[0]
        await changePhysicalWarehouseApi({
          warehouseCode: physicalWarehoue?.physicsWarehouseCode || '',
        })
        this.setPhysicalWarehouseInfo(physicalWarehoue!)
        this.changePhysicalWarehouseTime = Date.now()
      }
    },
    async checkPhysicalWarehouseCache() {
      const userStore = useUserStoreWithOut()
      const userInfo = userStore.getUserInfo
      const {
        clearWarehouseChoseCache,
        setWarehouseHasChoseCache,
        // setRememberCache,
        warehouseChoseRef,
      } = useWarehouseSwitch(userInfo?.userCode!)

      try {
        // 获取当前用户所拥有的物理仓信息，主要用于物理仓下拉
        const { records } = await getPhysicalWarehouseList({
          regionWarehouseCode: this.warehouseCode,
        })
        setUserPhysicalWarehouseList(records)

        if (!records.some((item) => item.physicsWarehouseCode === warehouseChoseRef.value)) {
          setWarehouseHasChoseCache('')
          clearWarehouseChoseCache()
          // setRememberCache(false)
        }

        // R348 获取当前区域仓下所有物理仓信息
        const { records: allRecords } = await getAllPhysicalWarehouseList({
          regionWarehouseCode: this.warehouseCode,
        })
        setAllPhysicalWarehouseList(allRecords)

        // R348 缓存所有区域仓信息
        const { records: regionRecords } = await getAllRegionWarehouseList({
          regionWarehouseCode: this.warehouseCode,
        })
        setAllRegionWarehouseList(regionRecords)
      } catch (err) {
        clearUserPhysicalWarehouseList()
        clearAllPhysicalWarehouseList()
        clearALLRegionWarehouseStorage()
        console.log(err)
      }
    },
  },
})

enableStoreHMRUpdate(useBaseStore)

export function useBaseStoreWithOut() {
  return useBaseStore(store)
}
